import React, { useState, useEffect, ChangeEvent, FocusEvent } from 'react';
import classNames from 'classnames';

import '@/styles/number-picker.less';

interface Props {
  min: number;
  max: number;
  step?: number;
  onBlur?: (value: number, event: FocusEvent<HTMLInputElement>) => void;
  onChange?: (value: number) => void;
  onMaxQuantity?: () => void;
  style?: {};
  value: number;
}

const NumberPicker: React.FC<Props> = ({
  min,
  max,
  step = 1,
  onBlur,
  onChange,
  onMaxQuantity,
  value,
  style = {},
}) => {
  const [number, setNumber] = useState(0);

  useEffect(() => {
    setNumber(value);
  }, [value]);

  const increment = (): void => {
    if (number + step <= max) {
      setNumber(number + step);
      if (onChange) {
        onChange(number + step);
      }
    } else {
      onMaxQuantity && onMaxQuantity();
    }
  };

  const decrement = (): void => {
    if (number - step >= min) {
      setNumber(number - step);
      if (onChange) {
        onChange(number - step);
      }
    }
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>): void => {
    if (onBlur) {
      onBlur(number, event);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const inputValue = event.currentTarget.value;
    if (/^\d*$/.test(inputValue)) {
      const parsedValue = inputValue === '' ? min : parseInt(inputValue, 10);
      const clampedValue = Math.min(Math.max(parsedValue, min), max);
      setNumber(clampedValue);
      if (onChange) {
        onChange(clampedValue);
      }
    }
  };

  return (
    <div className="number-picker-layout" style={style}>
      <div className="number-picker">
        <button
          onClick={decrement}
          className={classNames(['number-picker-button', { disabled: number - step < min }])}
        >
          -
        </button>
        <input
          type="text"
          className="number-picker-input"
          onBlur={handleBlur}
          onChange={handleChange}
          value={number}
        />
        <button
          onClick={increment}
          className={classNames(['number-picker-button', { disabled: number + step > max }])}
        >
          +
        </button>
      </div>
    </div>
  );
};

export default NumberPicker;
